import { logger, Logger } from './Logger'

class LocalStorage {
    private log: Logger

    constructor(log: Logger) {
        this.log = log
    }

    getBoolean(key: string, def?: boolean): boolean {
        const value = localStorage.getItem(key)
        if (value == null) {
            if (def != null) {
                return def
            } else {
                return false
            }
        } else {
            return !!value
        }
    }

    getInt(key: string, def?: number): number | null {
        const value = localStorage.getItem(key)
        if (value == null) {
            return def != null ? def : null
        } else {
            return parseInt(value)
        }
    }

    getFloat(key: string, def?: number): number | null {
        const value = localStorage.getItem(key)
        if (value == null) {
            return def != null ? def : null
        } else {
            const f = parseFloat(value)
            return !isNaN(f) ? f : def != null ? def : null
        }
    }

    getString(key: string): string | null {
        return localStorage.getItem(key)
    }

    getObject(key: string): { [key: string]: any } | null {
        try {
            const value = localStorage.getItem(key)
            if (value == null) {
                return null
            } else {
                return JSON.parse(value)
            }
        } catch (e) {
            this.log.error(
                'get',
                'failed to parse object! key=$[key}. Clearing this key because object is corrupted.',
                { e }
            )
            return null
        }
    }

    getAny(key: string): any {
        return localStorage.getItem(key)
    }

    getArray<T>(key: string): T[] | null {
        try {
            const value = localStorage.getItem(key)
            if (value == null) {
                return null
            } else {
                const data = JSON.parse(value)
                if (!Array.isArray(data)) {
                    this.log.error('getArray', 'failed to parse array! key=$[key]. Clearing this key.', {
                        e: new Error('not an array'),
                    })
                    return null
                }
                return data
            }
        } catch (e) {
            this.log.error('getArray', 'failed to parse array! key=$[key]. Clearing this key.', { e })
            return null
        }
    }

    set(key: string, value: boolean | number | string | { [key: string]: any }) {
        if (typeof value === 'boolean') {
            localStorage.setItem(key, value ? '1' : '')
        } else if (typeof value === 'number') {
            localStorage.setItem(key, value + '')
        } else if (typeof value === 'string') {
            localStorage.setItem(key, value)
        } else if (typeof value === 'object') {
            try {
                const str = JSON.stringify(value)
                localStorage.setItem(key, str)
            } catch (e) {
                this.log.error('set', 'failed to stringify object! key=$[key}. No-op.', { e })
            }
        }
    }

    delete(key: string | ((candidate: string) => boolean)) {
        if (typeof key === 'string') {
            localStorage.removeItem(key)
        } else if (typeof key === 'function') {
            Object.keys(localStorage).forEach((candidate) => {
                if (key(candidate)) {
                    localStorage.removeItem(candidate)
                }
            })
        }
    }

    getKeys() {
        return Object.keys(localStorage)
    }
}

const log = logger.createChildLogger({ name: 'LocalStorage' })

export const persistent = new LocalStorage(log)
